import { CurrentUser } from '#~/auth/synchronizer.jsx';
import { getActiveAuthJourney, getLastKnownUserId } from '#~/auth/auth-journey.js';
import { ActiveStudio } from '#~/analytics/active-studio.js';
import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import { getBootId } from '#~/logger/index.js';
import { v4 as uuidv4 } from 'uuid';
import {
  AGENT_PROPERTIES,
  ANALYTICS_ID_ENV_NAMES,
  COMMON_PROPERTIES,
  DEVICE_PROPERTIES,
  ENVELOPE_CLIENT,
  ENVELOPE_PROCESS_TYPE,
  RENDERER_ANALYTICS_PLATFORM,
  STUDIO_PROPERTIES
} from '../constants/event-properties.js';
import { applyLegacyPropertyAliases } from '../constants/property-consolidation.js';
import type { EventProperties } from '../types/properties.js';
import { resolveBuildVersion } from './build-version.js';

export const ANALYTICS_SESSION_STORAGE_KEY = 'analytics.session_id';

/** Read a shared identity without breaking web or hardened renderer builds. */
const readElectronEnv = (name: string): string | undefined => {
  const env = (
    globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } }
  ).window?.electron?.process?.env;
  const value = env?.[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

// Reuse the logger's page identity so web logs and analytics remain correlatable.
const getOrCreateWebLaunchId = (): string => getBootId();

const readSessionStorage = (key: string): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeSessionStorage = (key: string, value: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures.
  }
};

const getOrCreateSessionId = (): string => {
  const existing = readSessionStorage(ANALYTICS_SESSION_STORAGE_KEY);

  if (existing) {
    return existing;
  }

  // Reuse the frontend's secure UUID implementation.
  const next = uuidv4();
  writeSessionStorage(ANALYTICS_SESSION_STORAGE_KEY, next);
  return next;
};

export const resolveAnalyticsContext = (properties: EventProperties = {}): EventProperties => {
  const authJourney = getActiveAuthJourney();
  const currentUserId = CurrentUser.user?.id;
  const lastKnownUserId = getLastKnownUserId();

  // Caller value wins; otherwise Electron shares main's id and web stays per-tab.
  const resolvedSessionId =
    properties[COMMON_PROPERTIES.SESSION_ID_CANONICAL] ??
    properties[COMMON_PROPERTIES.SESSION_ID] ??
    readElectronEnv(ANALYTICS_ID_ENV_NAMES.SESSION_ID) ??
    getOrCreateSessionId();

  // One value for launch_id and boot_id keeps main, renderer, and logs correlatable.
  const resolvedLaunchId =
    properties[COMMON_PROPERTIES.LAUNCH_ID] ??
    properties[COMMON_PROPERTIES.BOOT_ID] ??
    readElectronEnv(ANALYTICS_ID_ENV_NAMES.BOOT_ID) ??
    getOrCreateWebLaunchId();

  const resolvedDeviceId =
    properties[DEVICE_PROPERTIES.DEVICE_ID_CANONICAL] ?? properties[DEVICE_PROPERTIES.DEVICE_ID];
  const resolvedAgentId =
    properties[AGENT_PROPERTIES.AGENT_ID_CANONICAL] ?? properties[AGENT_PROPERTIES.AGENT_ID];
  const resolvedStudioId =
    properties[STUDIO_PROPERTIES.STUDIO_ID_CANONICAL] ??
    properties[STUDIO_PROPERTIES.STUDIO_ID] ??
    ActiveStudio.id;
  // env is the ONE canonical spelling (canonical-event-map.json requires `env`, never
  // `environment`); accept either legacy input so a caller's value still wins.
  const resolvedEnv =
    properties[COMMON_PROPERTIES.ENV] ?? properties[COMMON_PROPERTIES.ENVIRONMENT] ?? config.env;
  // CPU architecture is a desktop-only machine fact, read ONLY from the Electron-main
  // IRIS_ARCH bridge — never from caller-supplied web input. The browser cannot detect
  // Apple Silicon (Safari and Chromium both report "Intel"), so a web send must omit
  // arch rather than carry a wrong value that would silently merge the chip cohorts.
  // That is why arch is required on the desktop-only boot_relaunch_loop but NOT on the
  // cross-client boot_started/boot_succeeded/boot_failed, which also fire on web.
  const resolvedArch = readElectronEnv(ANALYTICS_ID_ENV_NAMES.ARCH);
  // Drop caller `arch` unless the bridge set a value. `...properties` would otherwise
  // keep a web-injected "Intel"; an empty `{...(resolvedArch ? {arch} : {})}` cannot
  // delete a key already copied from the caller.
  const propertiesWithoutCallerArch: EventProperties = { ...properties };
  delete propertiesWithoutCallerArch[COMMON_PROPERTIES.ARCH];
  const resolvedDeviceBrandId = properties[DEVICE_PROPERTIES.DEVICE_BRAND_ID];
  const resolvedDeviceModelId = properties[DEVICE_PROPERTIES.DEVICE_MODEL_ID];
  const resolvedEmbedded =
    properties[DEVICE_PROPERTIES.IS_EMBEDDED] ?? properties.agentEmbedded ?? undefined;
  const resolvedAuthJourneyId =
    properties[COMMON_PROPERTIES.AUTH_JOURNEY_ID] ?? authJourney?.authJourneyId;
  const resolvedSource = properties[COMMON_PROPERTIES.SOURCE] ?? authJourney?.source;

  // Write each duplicated property ONCE under its canonical
  // snake_case spelling; the legacy alias is mirrored from this one value by
  // applyLegacyPropertyAliases below, so the two spellings can no longer diverge.
  // The legacy brand/model dual-write is intentionally gone — it was dead code
  // (assertCanonicalDeviceProps strips those keys before enrichment on the track()
  // path), and device cohort props are owned by device-context.ts's canonical shape.
  const canonicalContext: EventProperties = {
    ...propertiesWithoutCallerArch,
    ...(currentUserId ? { [COMMON_PROPERTIES.USER_ID_CANONICAL]: currentUserId } : {}),
    ...(lastKnownUserId ? { [COMMON_PROPERTIES.LAST_KNOWN_USER_ID]: lastKnownUserId } : {}),

    [COMMON_PROPERTIES.ENV]: resolvedEnv,
    [COMMON_PROPERTIES.VENDOR]: properties[COMMON_PROPERTIES.VENDOR] ?? config.vendor ?? 'unknown',
    // Preserve the renderer platform meaning; process_type distinguishes processes.
    [COMMON_PROPERTIES.PLATFORM]:
      properties[COMMON_PROPERTIES.PLATFORM] ??
      (isElectronApp() ? RENDERER_ANALYTICS_PLATFORM.ELECTRON : RENDERER_ANALYTICS_PLATFORM.WEB),
    // Stamp only when IRIS_ARCH is set. Caller arch was stripped above, so web/no-bridge
    // cannot keep a fabricated value even if properties originally contained one.
    ...(resolvedArch ? { [COMMON_PROPERTIES.ARCH]: resolvedArch } : {}),
    // The BUILD's identity, never the studio/client package file's version — the
    // package version answers "which package", and the question a reader actually has
    // is "did this row come from a build containing the fix".
    [COMMON_PROPERTIES.APP_VERSION]:
      properties[COMMON_PROPERTIES.APP_VERSION] ?? resolveBuildVersion(config.appVersion),

    // Keep client aligned with the journey surface while process_type marks the sender.
    [COMMON_PROPERTIES.CLIENT]:
      properties[COMMON_PROPERTIES.CLIENT] ??
      (isElectronApp() ? ENVELOPE_CLIENT.ELECTRON : ENVELOPE_CLIENT.STANDALONE_WEB),
    [COMMON_PROPERTIES.PROCESS_TYPE]:
      properties[COMMON_PROPERTIES.PROCESS_TYPE] ?? ENVELOPE_PROCESS_TYPE.RENDERER,
    [COMMON_PROPERTIES.BOOT_ID]: resolvedLaunchId,
    [COMMON_PROPERTIES.LAUNCH_ID]: resolvedLaunchId,

    [COMMON_PROPERTIES.SESSION_ID_CANONICAL]: resolvedSessionId,
    ...(resolvedAuthJourneyId
      ? { [COMMON_PROPERTIES.AUTH_JOURNEY_ID]: resolvedAuthJourneyId }
      : {}),
    ...(resolvedSource ? { [COMMON_PROPERTIES.SOURCE]: resolvedSource } : {}),

    ...(resolvedDeviceId ? { [DEVICE_PROPERTIES.DEVICE_ID_CANONICAL]: resolvedDeviceId } : {}),
    ...(resolvedAgentId ? { [AGENT_PROPERTIES.AGENT_ID_CANONICAL]: resolvedAgentId } : {}),
    ...(resolvedStudioId ? { [STUDIO_PROPERTIES.STUDIO_ID_CANONICAL]: resolvedStudioId } : {}),
    ...(resolvedDeviceBrandId
      ? { [DEVICE_PROPERTIES.DEVICE_BRAND_ID]: resolvedDeviceBrandId }
      : {}),
    ...(resolvedDeviceModelId
      ? { [DEVICE_PROPERTIES.DEVICE_MODEL_ID]: resolvedDeviceModelId }
      : {}),
    ...(resolvedEmbedded !== undefined ? { [DEVICE_PROPERTIES.IS_EMBEDDED]: resolvedEmbedded } : {})
  };

  // During the backward-compat parity window, emit the legacy alias alongside the
  // canonical spelling, mirrored from the single canonical value. Removal is gated.
  return applyLegacyPropertyAliases(canonicalContext);
};
